import { NgIf } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, EventEmitter, inject, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, finalize, throwError } from 'rxjs';
import { LoadingComponent } from '../../components/loading/loading.component';
import { AuthService, UsernameOrEmailCheckResponse } from '../../services/auth.service';
import { SharedModule } from '../../shared/modules/shared.module';
import * as Utils from '../../utils/email.util';
import { ForgotPasswordMessageComponent } from './success/forgot-password-message.component';
import { showSnackBar } from '../../utils/snackBar.ulti';
import { MigratedUserPromptComponent } from '../../components/migrated-user-prompt/migrated-user-prompt.component';
import { extractErrors } from '../../utils/extracErrors.util';

@Component({
  selector: 'app-forgot-password',
  imports: [
    ForgotPasswordMessageComponent,
    FormsModule,
    LoadingComponent,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatSuffix,
    NgIf,
    ReactiveFormsModule,
    SharedModule,
    TranslatePipe,
    MigratedUserPromptComponent
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss'
})
export class ForgotPasswordComponent {
  @Output() handleNext = new EventEmitter<{ username: string; step: number }>();

  protected form: FormGroup;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected get email(): any {
    return this.form.get('email');
  }
  protected isLoading = false;
  protected isShowMessage = false;
  protected loginHint = '';
  protected promptTitle = 'Account Migrated';
  protected readonly promptContent =
    'Since you have already migrated your account. Your username is now inactive, please try logging in with your email address & password.';

  private _snackBar = inject(MatSnackBar);
  private readonly _resetPasswordSuccessNotiMessage = 'A new reset email has been sent, if the account exists.';
  private readonly _resetPasswordFailNotiMessage = 'Something went wrong when sending reset password request';

  constructor(
    private _formBuilder: FormBuilder,
    private _authService: AuthService,
    private _translate: TranslateService
  ) {
    this.form = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  onSubmit = () => {
    if (this.form.valid) {
      const email = this.form.value.email;

      if (Utils.isEmail(email)) {
        this._handleCheckMigrated();
      }
    }
  };

  private _handleCheckMigrated() {
    const emailInput = this.form.value.email;
    this.isLoading = true;

    this._authService
      .checkUsernameOrEmail(emailInput)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this._extractErrors(error);

          return throwError(() => error);
        })
      )
      .subscribe(async (result: UsernameOrEmailCheckResponse) => {
        const { userExists, isUserMigrated, email } = result;

        switch (true) {
          case userExists && isUserMigrated:
            this._onUserMigrated(email);

            break;

          case userExists:
            this.handleForgotPassword(email);

            break;

          default:
            this._onUserNotExisted();

            break;
        }
      });
  }

  private _onUserMigrated(loginHint: string) {
    this.loginHint = loginHint;
    this.isLoading = false;
  }

  private _onUserNotExisted() {
    this.isLoading = false;
    this.isShowMessage = true;
  }

  handleForgotPassword = (email: string) => {
    this.isLoading = true;

    this._authService
      .forgotPassword(email)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isShowMessage = true;

          if (this.isShowMessage)
            showSnackBar({ type: 'email', message: this._resetPasswordSuccessNotiMessage, snackBar: this._snackBar, translate: this._translate });
        }),

        catchError((error) => {
          this.isLoading = false;
          this.isShowMessage = true;

          if (error.status === HttpStatusCode.InternalServerError)
            showSnackBar({ type: 'error', message: this._resetPasswordFailNotiMessage, snackBar: this._snackBar, translate: this._translate });

          return error;
        })
      )
      .subscribe();
  };

  private _extractErrors(apiResponse: HttpErrorResponse): void {
    this.isLoading = false;
    const { status } = apiResponse ?? {};

    extractErrors({ status, snackBar: this._snackBar, translate: this._translate });
  }
}
